import { useMemo } from "react";
import { STATUS_COLORS } from "../constants/statusColors";
import { DEFECT_STATUS_COLOR } from "../../defects/pages/DefectsViewV2Page";
import { addDays, computeDefectMarkerDate, computeTaskBarSpan, isWorkingDay, startOfDay } from "../utils/timelineBars";

const LABEL_WIDTH = 220;

function fmtDayLabel(d) {
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

function dayIndex(firstDay, date) {
  return Math.round((startOfDay(date) - firstDay) / 86400000);
}

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

// Mirrors TaskBar's/DefectMarker's own visibility checks below, so the
// "Assigned only" filter hides exactly the rows that would render nothing.
function isTaskVisibleInWindow(task, days, firstDay) {
  const span = computeTaskBarSpan(task);
  if (!span) return false;
  const rawStart = dayIndex(firstDay, span.startDate) + span.startFraction;
  const rawEnd = dayIndex(firstDay, span.endDate) + 1;
  const start = clamp(rawStart, 0, days.length);
  const end = clamp(rawEnd, 0, days.length);
  return end > start;
}

function isDefectVisibleInWindow(defect, days, firstDay) {
  const date = computeDefectMarkerDate(defect);
  if (!date) return false;
  const idx = dayIndex(firstDay, date);
  return idx >= 0 && idx < days.length;
}

function TaskBar({ task, days, firstDay }) {
  const span = computeTaskBarSpan(task);
  if (!span) return null;

  const rawStart = dayIndex(firstDay, span.startDate) + span.startFraction;
  const rawEnd = dayIndex(firstDay, span.endDate) + 1;
  const start = clamp(rawStart, 0, days.length);
  const end = clamp(rawEnd, 0, days.length);
  if (end <= start) return null;

  const colors = STATUS_COLORS[task.status] || STATUS_COLORS["Not Started"];
  const customerProject = [task.customer, task.project].filter(Boolean).join(" / ");
  const title = `${task.title || "(untitled task)"} — ${task.status}`
    + (customerProject ? `\n${customerProject}` : "")
    + `\nDue ${task.customer_due_date ? new Date(task.customer_due_date).toLocaleDateString() : "—"}\nEst. ${task.estimated_hours || 0}h ${task.estimated_minutes || 0}m`;

  return (
    <div
      className="ppt-bar"
      title={title}
      style={{
        left: `${(start / days.length) * 100}%`,
        width: `${((end - start) / days.length) * 100}%`,
        background: colors.dot,
        color: colors.color,
      }}
    >
      <span className="ppt-bar__label">{task.title}</span>
    </div>
  );
}

function DefectMarker({ defect, days, firstDay }) {
  const date = computeDefectMarkerDate(defect);
  if (!date) return null;
  const idx = dayIndex(firstDay, date);
  if (idx < 0 || idx >= days.length) return null;

  const color = DEFECT_STATUS_COLOR[defect.status] || "#777";
  const customerProject = [defect.customer_name, defect.project_name].filter(Boolean).join(" / ");
  const title = `${defect.defect_code || ""} ${defect.title || ""} — ${defect.status}`
    + (customerProject ? `\n${customerProject}` : "")
    + `\nDue ${new Date(defect.due_date).toLocaleDateString()}`;

  return (
    <div
      className="ppt-marker"
      title={title}
      style={{ left: `${((idx + 0.5) / days.length) * 100}%`, background: color, borderColor: color }}
    />
  );
}

export default function ProjectTimeline({ groups, weekOffset, windowDaysBack, windowDaysForward, assignedOnly }) {
  const days = useMemo(() => {
    const today = startOfDay(new Date());
    const anchor = addDays(today, weekOffset * 7);
    const out = [];
    for (let i = -windowDaysBack; i <= windowDaysForward; i++) out.push(addDays(anchor, i));
    return out;
  }, [weekOffset, windowDaysBack, windowDaysForward]);

  const firstDay = days[0];
  const today = startOfDay(new Date()).getTime();

  const visibleGroups = useMemo(() => {
    if (!assignedOnly) return groups;
    return groups
      .map((group) => ({
        ...group,
        people: group.people.filter((person) =>
          person.tasks.some((t) => isTaskVisibleInWindow(t, days, firstDay)) ||
          person.defects.some((d) => isDefectVisibleInWindow(d, days, firstDay))
        ),
      }))
      .filter((group) => group.people.length > 0);
  }, [groups, assignedOnly, days, firstDay]);

  return (
    <div className="ppt-timeline">
      <div className="ppt-legend">
        {Object.entries(STATUS_COLORS).map(([status, c]) => (
          <span key={status} className="ppt-legend__item">
            <span className="ppt-legend__swatch" style={{ background: c.dot }} /> {status}
          </span>
        ))}
        <span className="ppt-legend__sep" />
        {Object.entries(DEFECT_STATUS_COLOR).map(([status, color]) => (
          <span key={status} className="ppt-legend__item">
            <span className="ppt-legend__swatch ppt-legend__swatch--diamond" style={{ background: color }} /> {status}
          </span>
        ))}
      </div>

      <div className="ppt-tl-scroll">
        <div className="ppt-tl-inner">
          <div className="ppt-tl-header">
            <div className="ppt-tl-header-label" style={{ width: LABEL_WIDTH }} />
            <div className="ppt-tl-header-days">
              {days.map((d, i) => (
                <div
                  key={i}
                  className={`ppt-tl-daycell${isWorkingDay(d) ? "" : " ppt-tl-daycell--weekend"}${startOfDay(d).getTime() === today ? " ppt-tl-daycell--today" : ""}`}
                >
                  {fmtDayLabel(d)}
                </div>
              ))}
            </div>
          </div>

          {visibleGroups.length === 0 && (
            <div className="ppt-tl-empty">
              No one has a task or defect due in this window. Try "All resources" or a different week.
            </div>
          )}

          {visibleGroups.map((group) => (
            <div key={group.teamName} className="ppt-tl-team">
              <div className="ppt-tl-team-header">
                {group.teamName} <span className="ppt-tl-team-count">{group.people.length} people</span>
              </div>
              {group.people.map((person) => (
                <div key={person.displayName} className="ppt-tl-row">
                  <div className="ppt-tl-row-label" style={{ width: LABEL_WIDTH }}>{person.displayName}</div>
                  <div className="ppt-tl-row-track">
                    {days.map((d, i) => (
                      !isWorkingDay(d) && (
                        <div
                          key={i}
                          className="ppt-tl-weekend-col"
                          style={{ left: `${(i / days.length) * 100}%`, width: `${(1 / days.length) * 100}%` }}
                        />
                      )
                    ))}
                    {person.tasks.map((t) => (
                      <TaskBar key={t.id} task={t} days={days} firstDay={firstDay} />
                    ))}
                    {person.defects.map((d) => (
                      <DefectMarker key={d.id} defect={d} days={days} firstDay={firstDay} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
